<script setup lang="ts">
// The guided wizard. No signup at all: the backend mints an anonymous
// device cookie on the first AI call and meters the free allowance against
// the device + IP (email verification is on hold until a mail provider
// lands). The diagnosis mutation NEVER auto-retries — a retry would burn a
// second cap slot — and the idempotency key makes manual retries safe.
import { computed, ref } from 'vue'
import { NButton, NInput, NInputNumber, NModal, NProgress } from 'naive-ui'
import { useMutation } from '@tanstack/vue-query'
import treeJson from '@contracts/decision-tree.ror.v1.json'
import { useDiagnosticEngine } from '@/shared/diagnostic/engine'
import type { DecisionTree } from '@/shared/diagnostic/types'
import { postJson, ApiError } from '@/shared/api/client'
import type { DiagnosisResponse } from '@/shared/api/types'
import GlassCard from '@/shared/components/GlassCard.vue'

const tree = treeJson as DecisionTree
const emit = defineEmits<{ result: [DiagnosisResponse] }>()

const engine = useDiagnosticEngine(tree)

// Multi-select scratch state
const multiPicked = ref<string[]>([])
// Numeric / text / zip scratch state
const numberValue = ref<number | null>(null)
const textValue = ref('')

// One idempotency key per completed tree run.
let idempotencyKey = crypto.randomUUID()

// "Why we ask this" transparency modal (copy lives in the tree contract).
const whyOpen = ref(false)

const diagnose = useMutation({
    retry: false, // a silent retry could burn a second AI slot
    mutationFn: () =>
        postJson<DiagnosisResponse>('/api/diagnosis', {
            idempotency_key: idempotencyKey,
            tree_id: tree.id,
            tree_version: tree.version,
            answers: engine.answers.value,
        }),
    onSuccess: (res) => emit('result', res),
})

const errorInfo = computed(() => {
    const err = diagnose.error.value
    if (!err) return null
    if (err instanceof ApiError) {
        if (err.code === 'ai_cap_reached')
            return {
                title: 'Free AI limit reached',
                body: 'This device has used all of its free AI analyses. Running the AI costs us real money, so we cap it to keep the tool free for everyone.',
            }
        if (err.code === 'ai_unavailable')
            return {
                title: 'AI temporarily unavailable',
                body: 'The analysis service is paused right now. Your answers are safe — please try again a little later.',
            }
        return { title: 'Analysis failed', body: err.message }
    }
    return { title: 'Analysis failed', body: 'Something went wrong — you were not charged an interaction.' }
})

function selectOption(value: string) {
    engine.answer(value)
}

function submitMulti() {
    engine.answer([...multiPicked.value])
    multiPicked.value = []
}

function toggleMulti(value: string) {
    multiPicked.value = multiPicked.value.includes(value)
        ? multiPicked.value.filter((v) => v !== value)
        : [...multiPicked.value, value]
}

function submitNumber() {
    if (numberValue.value === null) return
    engine.answer(numberValue.value)
    numberValue.value = null
}

function submitText() {
    engine.answer(textValue.value.trim())
    textValue.value = ''
}

function submitZip(skip = false) {
    engine.answer(skip ? null : textValue.value.trim())
    textValue.value = ''
}

function startOver() {
    engine.reset()
    idempotencyKey = crypto.randomUUID()
    diagnose.reset()
}

/** Jump straight into the tree from a Common Problems card. */
function startWithSymptom(symptom: string) {
    startOver()
    engine.answer(symptom)
}

defineExpose({ startWithSymptom, startOver })

const node = engine.currentNode
</script>

<template>
    <section id="diagnosis" class="site-section alt">
        <div class="site-content narrow">
            <h2>Guided Diagnosis</h2>
            <p class="muted">
                A few structured questions, then one AI analysis. No open-ended chat — that keeps
                it fast, consistent, and cheap enough to stay free.
            </p>

            <GlassCard class="wizard">
                <!-- In-progress: question flow -->
                <template v-if="!engine.done.value && node">
                    <NProgress
                        type="line"
                        :percentage="Math.round(engine.progress.value * 100)"
                        :show-indicator="false"
                        class="progress"
                    />
                    <div class="q-head">
                        <p class="q-count muted">Question {{ engine.answers.value.length + 1 }}</p>
                        <button v-if="node.why" class="why-link" type="button" @click="whyOpen = true">
                            ⓘ Why we ask this
                        </button>
                    </div>
                    <h3>{{ node.prompt }}</h3>
                    <p v-if="node.help" class="muted help">{{ node.help }}</p>

                    <NModal v-model:show="whyOpen" preset="card" title="Why we ask this" style="max-width: 30rem">
                        <p>{{ node.why }}</p>
                        <p class="muted why-data-use">
                            Your answers feed one AI analysis and nothing else — never sold, never
                            used for marketing. ZIP and free-text are optional and length-capped.
                        </p>
                    </NModal>

                    <div v-if="node.kind === 'single_select'" class="options">
                        <button
                            v-for="opt in node.options"
                            :key="opt.value"
                            class="option"
                            type="button"
                            @click="selectOption(opt.value)"
                        >
                            {{ opt.label }}
                        </button>
                    </div>

                    <div v-else-if="node.kind === 'multi_select'" class="options">
                        <button
                            v-for="opt in node.options"
                            :key="opt.value"
                            class="option"
                            :class="{ picked: multiPicked.includes(opt.value) }"
                            type="button"
                            @click="toggleMulti(opt.value)"
                        >
                            {{ multiPicked.includes(opt.value) ? '✓ ' : '' }}{{ opt.label }}
                        </button>
                        <NButton type="primary" size="large" class="continue" @click="submitMulti">
                            {{ multiPicked.length ? 'Continue' : 'None of these — continue' }}
                        </NButton>
                    </div>

                    <div v-else-if="node.kind === 'numeric'" class="inline-input">
                        <NInputNumber
                            v-model:value="numberValue"
                            :min="node.min"
                            :max="node.max"
                            :placeholder="node.unit ?? ''"
                            size="large"
                        />
                        <span v-if="node.unit" class="muted">{{ node.unit }}</span>
                        <NButton type="primary" size="large" :disabled="numberValue === null" @click="submitNumber">
                            Continue
                        </NButton>
                    </div>

                    <div v-else-if="node.kind === 'zip'" class="inline-input">
                        <NInput
                            v-model:value="textValue"
                            placeholder="e.g. 34110"
                            inputmode="numeric"
                            :maxlength="5"
                            size="large"
                            style="max-width: 160px"
                            @keyup.enter="submitZip()"
                        />
                        <NButton
                            type="primary"
                            size="large"
                            :disabled="!/^\d{5}$/.test(textValue.trim())"
                            @click="submitZip()"
                        >
                            Continue
                        </NButton>
                        <NButton v-if="node.optional" quaternary size="large" @click="submitZip(true)">
                            Skip
                        </NButton>
                    </div>

                    <div v-else-if="node.kind === 'text_short'" class="inline-input column">
                        <NInput
                            v-model:value="textValue"
                            type="textarea"
                            :maxlength="node.max_len ?? 200"
                            :autosize="{ minRows: 2, maxRows: 4 }"
                            placeholder="A sentence or two…"
                            size="large"
                        />
                        <NButton
                            type="primary"
                            size="large"
                            :disabled="!textValue.trim()"
                            @click="submitText"
                        >
                            Continue
                        </NButton>
                    </div>

                    <button v-if="engine.canGoBack.value" class="linkish" type="button" @click="engine.back()">
                        ← Back
                    </button>
                </template>

                <!-- Tree complete: straight to the analysis — no signup. -->
                <template v-else-if="engine.done.value">
                    <h3>Ready for your analysis</h3>
                    <p class="muted">
                        One AI request, generated from your {{ engine.answers.value.length }}
                        answers. Free, no signup — each device gets a generous allowance.
                    </p>
                    <div v-if="errorInfo" class="error-panel">
                        <strong>{{ errorInfo.title }}</strong>
                        <p class="muted">{{ errorInfo.body }}</p>
                    </div>
                    <div class="inline-input">
                        <NButton
                            type="primary"
                            size="large"
                            :loading="diagnose.isPending.value"
                            @click="diagnose.mutate()"
                        >
                            {{ diagnose.isError.value ? 'Try Again' : 'Run My Free AI Analysis' }}
                        </NButton>
                        <NButton quaternary size="large" @click="startOver">Start Over</NButton>
                    </div>
                </template>
            </GlassCard>
        </div>
    </section>
</template>

<style scoped lang="scss">
.alt {
    background: var(--site-bg-alt);
}

.narrow {
    max-width: 46rem;
}

.wizard {
    margin-top: var(--site-space-lg);
    min-height: 320px;

    .progress {
        margin-bottom: var(--site-space-md);
    }

    .q-head {
        display: flex;
        align-items: baseline;
        justify-content: space-between;
        gap: var(--site-space-md);
    }

    .q-count {
        font-size: 0.8rem;
        text-transform: uppercase;
        letter-spacing: 2px;
        margin-bottom: var(--site-space-xs);
    }

    .why-link {
        background: none;
        border: none;
        cursor: pointer;
        font-size: 0.85rem;
        color: var(--site-accent);
        padding: 0;

        &:hover {
            color: var(--site-accent-strong);
        }
    }

    .help {
        font-size: 0.95rem;
    }
}

.options {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: var(--site-space-sm);
    margin-top: var(--site-space-md);
}

.option {
    padding: 0.9em 1.1em;
    border: 1px solid var(--site-border);
    border-radius: var(--site-radius-md);
    background: var(--site-surface-raised);
    color: var(--site-text);
    font-family: var(--site-font-body);
    font-size: 0.98rem;
    text-align: left;
    cursor: pointer;
    transition: border-color 0.12s ease, transform 0.12s ease;

    &:hover {
        border-color: var(--site-accent);
        transform: translateY(-1px);
    }

    &.picked {
        border-color: var(--site-accent);
        background: var(--site-accent-soft);
    }
}

.continue {
    grid-column: 1 / -1;
    justify-self: start;
}

.inline-input {
    display: flex;
    align-items: center;
    gap: var(--site-space-sm);
    margin-top: var(--site-space-md);

    &.column {
        flex-direction: column;
        align-items: stretch;
    }
}

.linkish {
    background: none;
    border: none;
    padding: 0;
    margin-top: var(--site-space-lg);
    color: var(--site-text-muted);
    cursor: pointer;
    font-size: 0.9rem;

    &:hover {
        color: var(--site-accent);
    }
}

.why-data-use {
    font-size: 0.85rem;
    font-style: italic;
    margin-top: var(--site-space-md);
}

.answers-safe {
    margin-top: var(--site-space-md);
    font-size: 0.85rem;
}

.error-panel {
    border: 1px solid var(--site-danger);
    border-radius: var(--site-radius-md);
    padding: var(--site-space-md);
    margin: var(--site-space-md) 0;

    p {
        margin: var(--site-space-xs) 0 0;
    }
}
</style>
